import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input, Space, Tooltip, Avatar, Typography, Drawer, Progress } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  EditOutlined,
  ShareAltOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  AppstoreOutlined,
  CaretRightOutlined,
  PauseOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import Swal from 'sweetalert2';
import { LARAVEL_BACKEND_API_URL } from '../../Helper';

const CLOSE_CURSOR = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><circle cx='16' cy='16' r='14' fill='red' stroke='white' stroke-width='1.5'/><path d='M11 11l10 10M21 11L11 21' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") 16 16, pointer`;

const { Paragraph } = Typography;

const FDCamPreviewModal = ({
  visible,
  onClose,
  mediaList = [],
  initialIndex = 0,
  project = {},
  inspector = {},
  mediaType = 'image', // 'image' or 'video'
  onUpdateDescription,
  onShare,
  onEditImage,
  onDelete
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  
  // Image manipulation states
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  
  // Description edit states
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState('');

  // Panning state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Button loading state
  const [loadingAction, setLoadingAction] = useState(null);

  // Slideshow and Thumbnails state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playProgress, setPlayProgress] = useState(0);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const thumbnailContainerRef = useRef(null);

  // Thumbnail drag scroll state
  const [isThumbDragging, setIsThumbDragging] = useState(false);
  const [thumbDragStartX, setThumbDragStartX] = useState(0);
  const [thumbScrollLeft, setThumbScrollLeft] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setDrawerVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Thumbnail Auto-Scroll
  useEffect(() => {
    if (showThumbnails && thumbnailContainerRef.current) {
      const activeThumb = thumbnailContainerRef.current.children[currentIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIndex, showThumbnails]);

  // Reset states when index changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
    setScale(1);
    setRotation(0);
    setIsEditingDesc(false);
    setPosition({ x: 0, y: 0 });
    setIsPlaying(false);
    setPlayProgress(0);
  }, [initialIndex, visible]);

  useEffect(() => {
    if (mediaList[currentIndex]) {
      setDescValue(mediaList[currentIndex].description || '');
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setIsEditingDesc(false);
    }
  }, [currentIndex, mediaList]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!visible || isEditingDesc) return;
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, currentIndex, isEditingDesc, mediaList.length]);

  // Auto Slideshow effect
  useEffect(() => {
    let interval;
    let progressInterval;
    if (isPlaying) {
      setPlayProgress(0);
      progressInterval = setInterval(() => {
        setPlayProgress(prev => {
          if (prev >= 100) return 0;
          return prev + (100 / 30); // reaches 100 in 3 seconds (30 * 100ms)
        });
      }, 100);

      interval = setInterval(() => {
        setCurrentIndex(prev => {
          setPlayProgress(0); // reset progress on next slide
          if (prev < mediaList.length - 1) {
            return prev + 1;
          }
          return 0; // Loop back to the first image
        });
      }, 3000);
    } else {
      setPlayProgress(0);
    }
    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, [isPlaying, mediaList.length]);

  // Fullscreen effect listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  if (!visible || !mediaList || mediaList.length === 0) return null;

  const currentMedia = mediaList[currentIndex];
  if (!currentMedia) return null;

  const isVideo = mediaType === 'video';

  const handleNext = () => {
    if (currentIndex < mediaList.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.5, 10));
  const handleZoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.5, 0.5);
      if (newScale <= 1) setPosition({ x: 0, y: 0 }); // reset pan on small scale
      return newScale;
    });
  };
  const handleRotateLeft = () => setRotation(prev => prev - 90);
  const handleRotateRight = () => setRotation(prev => prev + 90);

  // Mouse handlers for panning
  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault(); // prevent default image drag
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    if (scale <= 1 && e.deltaY > 0) return; // don't zoom out if already at 1x or less (to prevent shrinking too much accidentally unless they use the button)
    
    // Smooth zoom for mouse wheel
    if (e.deltaY < 0) {
      setScale(prev => Math.min(prev + 0.1, 10));
    } else {
      setScale(prev => {
        const newScale = Math.max(prev - 0.1, 0.5);
        if (newScale <= 1) setPosition({ x: 0, y: 0 });
        return newScale;
      });
    }
  };

  const handleSaveDescription = () => {
    if (onUpdateDescription) {
      onUpdateDescription(descValue, currentMedia.description, currentMedia.id, mediaType);
    }
    setIsEditingDesc(false);
  };

  const handleEditClick = async () => {
    setLoadingAction('edit');
    try {
      if (onEditImage) {
        await onEditImage(currentMedia);
      }
      onClose();
    } catch (err) {
      console.error("Error editing image:", err);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleShareClick = async () => {
    setLoadingAction('share');
    try {
      if (onShare) {
        await onShare(mediaType, currentMedia);
      }
    } catch (err) {
      console.error("Error sharing:", err);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      const isImage = mediaType === 'image';
      const itemLabel = isImage ? 'Photo' : 'Video';
      Swal.fire({
        title: `Move ${itemLabel} to Recycle Bin?`,
        text: `${isImage ? 'Photos' : 'Videos'} will be moved to the Recycle Bin. You can restore them back to the project within 30 days.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#0a497b',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, Move to Recycle Bin',
        cancelButtonText: 'Cancel',
      }).then(async (result) => {
        if (result.isConfirmed) {
          setLoadingAction('delete');
          try {
            await onDelete(currentMedia.id, mediaType);
            onClose();
          } catch (err) {
            console.error("Error deleting:", err);
          } finally {
            setLoadingAction(null);
          }
        }
      });
    }
  };

  // Thumbnail Scrolling Handlers
  const handleThumbMouseDown = (e) => {
    if (!thumbnailContainerRef.current) return;
    setIsThumbDragging(true);
    setThumbDragStartX(e.pageX - thumbnailContainerRef.current.offsetLeft);
    setThumbScrollLeft(thumbnailContainerRef.current.scrollLeft);
  };

  const handleThumbMouseMove = (e) => {
    if (!isThumbDragging || !thumbnailContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - thumbnailContainerRef.current.offsetLeft;
    const walk = (x - thumbDragStartX) * 1.5; 
    thumbnailContainerRef.current.scrollLeft = thumbScrollLeft - walk;
  };

  const handleThumbMouseUp = () => {
    setIsThumbDragging(false);
  };

  const handleThumbWheel = (e) => {
    if (thumbnailContainerRef.current) {
      thumbnailContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  const handleCancelDescription = () => {
    setIsEditingDesc(false);
    setDescValue(currentMedia?.description || '');
  };

  const getMediaUrl = () => {
    if (isVideo) {
      return currentMedia.source_url;
    }
    return currentMedia.image || currentMedia.url;
  };

  const profile = inspector?.profiles?.[0];
  
  // Safe formatting for date
  const mediaDate = currentMedia.created_at || currentMedia.createdAt;
  const dateFormat = inspector?.preferred_date_format || profile?.preferred_date_format || 'DD/MM/YYYY';
  const formattedDate = mediaDate ? moment(mediaDate).format(`${dateFormat}, hh:mm A`) : '';

  const uploaderName = profile?.insp_fullname 
    || (inspector?.first_name ? `${inspector.first_name} ${inspector.last_name || ''}`.trim() : null)
    || inspector?.display_name 
    || inspector?.name 
    || inspector?.user_name 
    || inspector?.username 
    || project?.user?.name 
    || 'Unknown User';

  const uploaderAvatar = inspector?.profile_pic 
    || profile?.logo_url 
    || inspector?.profile_image 
    || inspector?.avatar;

  const avatarSrc = uploaderAvatar && !uploaderAvatar.startsWith('http') && !uploaderAvatar.startsWith('data:')
    ? `${LARAVEL_BACKEND_API_URL}${uploaderAvatar.startsWith('/') ? '' : '/'}${uploaderAvatar}`
    : uploaderAvatar;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      keyboard={!isFullscreen}
      width="100%"
      zIndex={990}
      style={{ top: 0, padding: 0, margin: 0, height: '100vh', maxWidth: '100%' }}
      className="fdcam-preview-modal"
      bodyStyle={{ height: '100vh', padding: 0, display: 'flex', overflow: 'hidden' }}
      destroyOnClose
    >
      {/* LEFT PANEL - MEDIA VIEWER */}
      <div className="fdcam-modal-left" style={{ userSelect: 'none' }}>
        
        {/* Navigation Arrows */}
        {currentIndex > 0 && (
          <Button className="nav-arrow prev-arrow" shape="circle" icon={<LeftOutlined />} onClick={handlePrev} />
        )}
        {currentIndex < mediaList.length - 1 && (
          <Button className="nav-arrow next-arrow" shape="circle" icon={<RightOutlined />} onClick={handleNext} />
        )}

        {/* Media Container */}
        <div className="media-container">
          {isVideo ? (
            <div 
              className="video-wrapper"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: CLOSE_CURSOR
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  onClose();
                }
              }}
            >
              <video 
                src={getMediaUrl()} 
                controls 
                autoPlay 
                style={{ maxWidth: '100%', maxHeight: '100%', outline: 'none', cursor: 'default' }}
              />
            </div>
          ) : (
            <div 
              className="image-wrapper" 
              style={{ 
                cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : CLOSE_CURSOR,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
              onClick={(e) => {
                // Only close if clicked exactly on the background and not in zoomed state
                if (e.target === e.currentTarget && scale === 1) {
                  onClose();
                }
              }}
            >
              <img 
                src={getMediaUrl()} 
                alt="preview"
                draggable={false}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.2s ease-in-out',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
              />
            </div>
          )}
        </div>

        {/* Top Toolbar (Floating Pill) */}
        {!isVideo && (
          <div className="fdcam-modal-toolbar" style={{
            position: 'absolute',
            top: isMobile ? '16px' : '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(20, 20, 20, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: isMobile ? '8px 12px' : '10px 20px',
            borderRadius: '40px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            zIndex: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            gap: isMobile ? '8px' : '12px',
            maxWidth: isMobile ? 'calc(100vw - 100px)' : '90vw',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <style>
              {`
                .fdcam-modal-toolbar::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            
            <AppstoreOutlined onClick={() => setShowThumbnails(!showThumbnails)} style={{ fontSize: isMobile ? '16px' : '18px', cursor: 'pointer', color: showThumbnails ? '#1677ff' : '#fff', opacity: 0.85, flexShrink: 0 }} title="Toggle Thumbnails" />
            
            {!isMobile && (
              <>
                <div style={{ width: 1, height: 16, background: 'rgba(255, 255, 255, 0.2)', margin: '0 2px', flexShrink: 0 }} />
                {isFullscreen ? (
                  <FullscreenExitOutlined onClick={handleToggleFullscreen} style={{ fontSize: '18px', cursor: 'pointer', color: '#fff', opacity: 0.85, flexShrink: 0 }} title="Exit Full Screen" />
                ) : (
                  <FullscreenOutlined onClick={handleToggleFullscreen} style={{ fontSize: '18px', cursor: 'pointer', color: '#fff', opacity: 0.85, flexShrink: 0 }} title="Full Screen" />
                )}
              </>
            )}

            <div style={{ width: 1, height: 16, background: 'rgba(255, 255, 255, 0.2)', margin: '0 2px', flexShrink: 0 }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexShrink: 0 }}>
              <ZoomOutOutlined onClick={handleZoomOut} style={{ fontSize: isMobile ? '16px' : '18px', cursor: 'pointer', color: '#fff', opacity: 0.85 }} />
              <span style={{ color: '#fff', fontSize: isMobile ? '12px' : '14px', fontWeight: '500', minWidth: isMobile ? '36px' : '46px', whiteSpace: 'nowrap', textAlign: 'center', userSelect: 'none' }}>
                {Math.round(scale * 100)}%
              </span>
              <ZoomInOutlined onClick={handleZoomIn} style={{ fontSize: isMobile ? '16px' : '18px', cursor: 'pointer', color: '#fff', opacity: 0.85 }} />
            </div>

            <div style={{ width: 1, height: 16, background: 'rgba(255, 255, 255, 0.2)', margin: '0 2px', flexShrink: 0 }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexShrink: 0 }}>
              <RotateLeftOutlined onClick={handleRotateLeft} style={{ fontSize: isMobile ? '16px' : '18px', cursor: 'pointer', color: '#fff', opacity: 0.85 }} />
              <RotateRightOutlined onClick={handleRotateRight} style={{ fontSize: isMobile ? '16px' : '18px', cursor: 'pointer', color: '#fff', opacity: 0.85 }} />
            </div>

            <div style={{ width: 1, height: 16, background: 'rgba(255, 255, 255, 0.2)', margin: '0 2px', flexShrink: 0 }} />
            
            {isPlaying ? (
              <div style={{ position: 'relative', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: 0.85, flexShrink: 0 }} onClick={() => setIsPlaying(false)} title="Pause Slideshow">
                <Progress 
                  type="circle" 
                  percent={playProgress} 
                  showInfo={false} 
                  width={isMobile ? 20 : 24}
                  strokeColor="#fff" 
                  trailColor="rgba(255,255,255,0.2)" 
                  strokeWidth={10}
                  style={{ position: 'absolute', top: isMobile ? 0 : -2, left: isMobile ? 0 : -2 }}
                />
                <PauseOutlined style={{ fontSize: '10px', color: '#fff' }} />
              </div>
            ) : (
              <CaretRightOutlined onClick={() => setIsPlaying(true)} style={{ fontSize: isMobile ? '16px' : '18px', cursor: 'pointer', color: '#fff', opacity: 0.85, flexShrink: 0 }} title="Play Slideshow" />
            )}

            <div style={{ width: 1, height: 16, background: 'rgba(255, 255, 255, 0.2)', margin: '0 2px', flexShrink: 0 }} />

            <div className="counter-badge" style={{ 
              color: 'rgba(255, 255, 255, 0.85)', 
              fontWeight: '500',
              fontSize: isMobile ? '12px' : '14px',
              letterSpacing: '0.5px',
              userSelect: 'none',
              flexShrink: 0
            }}>
              {currentIndex + 1} / {mediaList.length}
            </div>
          </div>
        )}
        
        {/* Thumbnails Bar */}
        {showThumbnails && !isVideo && (
          <div 
            className="thumbnails-bar" 
            ref={thumbnailContainerRef}
            onMouseDown={handleThumbMouseDown}
            onMouseMove={handleThumbMouseMove}
            onMouseUp={handleThumbMouseUp}
            onMouseLeave={handleThumbMouseUp}
            onWheel={handleThumbWheel}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              display: 'flex',
              padding: '12px 24px',
              gap: '4px',
              overflowX: 'auto',
              overflowY: 'hidden',
              zIndex: 10,
              justifyContent: mediaList.length < 10 ? 'center' : 'flex-start',
              scrollbarWidth: 'none', // hide scrollbar for firefox
              msOverflowStyle: 'none', // hide scrollbar for IE
              cursor: isThumbDragging ? 'grabbing' : 'grab',
              userSelect: 'none'
            }}
          >
            {mediaList.map((media, idx) => (
              <img 
                key={media.id || idx}
                src={media.image || media.url || media.source_url}
                alt={`thumbnail ${idx}`}
                draggable={false}
                onClick={() => setCurrentIndex(idx)}
                style={{
                  height: '47px',
                  minWidth: '68px',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  border: currentIndex === idx ? '2px solid #1677ff' : '2px solid transparent',
                  borderRadius: '2px',
                  opacity: currentIndex === idx ? 1 : 0.4,
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  userSelect: 'none'
                }}
              />
            ))}
          </div>
        )}
        
        {/* Mobile Info Button and Mobile Close Button */}
        {isMobile && (
          <>
            <Button 
              type="primary" 
              shape="circle" 
              icon={<InfoCircleOutlined />} 
              size="large"
              onClick={() => setDrawerVisible(true)}
              style={{ position: 'absolute', bottom: 24, right: 24, zIndex: 100 }}
            />
            <Button 
              type="text" 
              icon={<CloseOutlined style={{ fontSize: 20, color: 'white' }} />} 
              onClick={onClose}
              style={{ position: 'absolute', top: 16, right: 16, zIndex: 100, background: 'rgba(0,0,0,0.5)' }}
            />
          </>
        )}
      </div>

      {/* RIGHT PANEL - SIDEBAR */}
      {!isMobile ? (
        <div className="fdcam-modal-right">
          {/* Close button at top right */}
          <Button 
            type="text" 
            icon={<CloseOutlined style={{ fontSize: 20 }} />} 
            className="close-btn"
            onClick={onClose}
          />

          <div className="sidebar-content">
            {/* Header Info */}
            <div className="header-info">
              <h2 className="project-name" style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 8px 0', lineHeight: '1.3' }}>{project?.name || 'Project Name'}</h2>
              <div className="project-location" style={{ color: '#666', marginBottom: '8px', fontSize: '13px' }}>
                <EnvironmentOutlined style={{ marginRight: 6 }} /> 
                {project ? [project.address, project.city, project.state, project.postal_code].filter(Boolean).join(', ') : 'No location provided'}
              </div>

              {formattedDate && (
                <div className="media-date" style={{ color: '#888', marginBottom: '16px', fontSize: '13px' }}>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  {formattedDate}
                </div>
              )}
              
              <div className="uploader-info mt-3" style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <Avatar src={avatarSrc ? avatarSrc : undefined} size="default" style={{ backgroundColor: '#1890ff', marginRight: 12 }}>
                  {!avatarSrc && uploaderName.charAt(0).toUpperCase()}
                </Avatar>
                <div className="uploader-details" style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="name" style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>{uploaderName}</span>
                </div>
              </div>
            </div>

            <div className="divider" style={{ height: '1px', background: '#f0f0f0', margin: '16px 0' }} />

            {/* Actions */}
            <div className="action-buttons" style={{ display: 'flex', gap: '8px' }}>
              {!isVideo && (
                <Button type="primary" icon={<EditOutlined />} loading={loadingAction === 'edit'} onClick={handleEditClick} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 8px' }}>
                  Edit
                </Button>
              )}
              <Button type="default" icon={<ShareAltOutlined />} loading={loadingAction === 'share'} onClick={handleShareClick} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 8px', backgroundColor: '#f0f0f0', border: 'none' }}>
                Share
              </Button>
              <Button type="primary" danger icon={<DeleteOutlined />} loading={loadingAction === 'delete'} onClick={handleDeleteClick} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 8px' }}>
                Delete
              </Button>
            </div>

            <div className="divider" style={{ height: '1px', background: '#f0f0f0', margin: '16px 0' }} />

            {/* Description Area */}
            <div className="description-area" style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>Description</h3>
                {!isEditingDesc && (
                  <Button type="text" icon={<EditOutlined />} onClick={() => setIsEditingDesc(true)} size="small">
                    Edit
                  </Button>
                )}
              </div>
              
              {isEditingDesc ? (
                <div className="description-editor">
                  <Input.TextArea 
                    value={descValue}
                    onChange={(e) => setDescValue(e.target.value)}
                    rows={4}
                    style={{ marginBottom: '12px' }}
                    placeholder="Add a description..."
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button onClick={handleCancelDescription}>Cancel</Button>
                    <Button type="primary" onClick={handleSaveDescription}>Save</Button>
                  </div>
                </div>
              ) : (
                <div className="description-text" style={{ 
                  background: '#f9f9f9', 
                  padding: '12px', 
                  borderRadius: '6px',
                  minHeight: '80px'
                }}>
                  {currentMedia.description ? (
                    <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {currentMedia.description}
                    </Paragraph>
                  ) : (
                    <span style={{ color: '#aaa', fontStyle: 'italic' }}>No description provided.</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <Drawer
          title="Details & Actions"
          placement="bottom"
          height="80vh"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ padding: '16px' }}
        >
          <div className="sidebar-content">
            {/* Header Info */}
            <div className="header-info">
              <h2 className="project-name" style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', lineHeight: '1.3' }}>{project?.name || 'Project Name'}</h2>
              <div className="project-location" style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
                <EnvironmentOutlined style={{ marginRight: 6 }} /> 
                {project ? [project.address, project.city, project.state, project.postal_code].filter(Boolean).join(', ') : 'No location provided'}
              </div>
              
              <div className="uploader-info mt-3" style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <Avatar src={avatarSrc ? avatarSrc : undefined} size="default" style={{ backgroundColor: '#1890ff', marginRight: 12 }}>
                  {!avatarSrc && uploaderName.charAt(0).toUpperCase()}
                </Avatar>
                <div className="uploader-details" style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="name" style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>{uploaderName}</span>
                  {formattedDate && <span className="date" style={{ fontSize: '12px', color: '#888' }}>{formattedDate}</span>}
                </div>
              </div>
            </div>

            <div className="divider" style={{ height: '1px', background: '#f0f0f0', margin: '16px 0' }} />

            {/* Actions */}
            <div className="action-buttons" style={{ display: 'flex', gap: '8px' }}>
              {!isVideo && (
                <Button type="primary" icon={<EditOutlined />} loading={loadingAction === 'edit'} onClick={handleEditClick} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 8px' }}>
                  Edit
                </Button>
              )}
              <Button type="default" icon={<ShareAltOutlined />} loading={loadingAction === 'share'} onClick={handleShareClick} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 8px', backgroundColor: '#f0f0f0', border: 'none' }}>
                Share
              </Button>
              <Button type="primary" danger icon={<DeleteOutlined />} loading={loadingAction === 'delete'} onClick={handleDeleteClick} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 8px' }}>
                Delete
              </Button>
            </div>

            <div className="divider" style={{ height: '1px', background: '#f0f0f0', margin: '16px 0' }} />

            {/* Description Area */}
            <div className="description-area" style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>Description</h3>
                {!isEditingDesc && (
                  <Button type="text" icon={<EditOutlined />} onClick={() => setIsEditingDesc(true)} size="small">
                    Edit
                  </Button>
                )}
              </div>
              
              {isEditingDesc ? (
                <div className="description-editor">
                  <Input.TextArea 
                    value={descValue}
                    onChange={(e) => setDescValue(e.target.value)}
                    rows={4}
                    style={{ marginBottom: '12px' }}
                    placeholder="Add a description..."
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button onClick={handleCancelDescription}>Cancel</Button>
                    <Button type="primary" onClick={handleSaveDescription}>Save</Button>
                  </div>
                </div>
              ) : (
                <div className="description-text" style={{ 
                  background: '#f9f9f9', 
                  padding: '12px', 
                  borderRadius: '6px',
                  minHeight: '80px'
                }}>
                  {currentMedia.description ? (
                    <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {currentMedia.description}
                    </Paragraph>
                  ) : (
                    <span style={{ color: '#aaa', fontStyle: 'italic' }}>No description provided.</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </Drawer>
      )}
    </Modal>
  );
};

export default FDCamPreviewModal;
